import type { Cable, Design, EquipmentFace, PlacedEquipment, Rack } from '../../types';
import type {
  CableLogicState,
  DeviceLogicState,
  DeviceModel,
  LogicIssue,
  LogicIssueCategory,
  LogicIssueCode,
  LogicOverview,
  LogicResult,
  LogicSeverity,
  PoEStandard,
  PortLogicState,
  PowerInput,
  PowerInputKind,
} from '../../types/deviceModel';
import {
  POE_DEFAULT_WATTS,
  POE_NOMINAL_VOLTAGE,
  POE_STANDARD_LABEL,
  isCopperKind,
  isFiberKind,
  isPowerKind,
  normalizePortKind,
  portLogicKey,
  selfDrawWattsAtVoltage,
  isPowerInputEnabled,
  activePowerInputs,
  inputCapacityWatts,
  typicalVoltageOfInput,
  derivedPsuOutput,
  derivedAcOutput,
  powerCablesCompatible,
  powerPortFamily,
  inverterRequiresBatteryToStart,
} from '../../types/deviceModel';
import { parsePortEndpointId } from '../../utils/portNamingSchemes';
import { placedIdFromEndpoint } from '../../utils/cableGeometry';
import { getCatalogDeviceModel, PSU_CATALOG } from '../../data/deviceModels';
import type { FacedPort } from './layoutPorts';
import { resolvePlacedEquipment, resolveRackEquipment, type ResolvedDevice } from './resolveModel';
import { compareLayoutToModel } from './layoutSync';
import { analyzeFamilyAtPair, findSharedPvTrackers, inverterBatteryPorts, inverterMpptPorts } from './polarityGraph';
import { summarizeBackupPower } from './backupPower';

function issue(
  code: LogicIssueCode,
  severity: LogicSeverity,
  category: LogicIssueCategory,
  message: string,
  extra: Partial<LogicIssue> = {}
): LogicIssue {
  return {
    id: `${code}:${extra.equipmentId || extra.cableId || extra.portNumber || message}`,
    code,
    severity,
    category,
    message,
    ...extra,
  };
}

const POWER_FAIL_PRIORITY: Partial<Record<LogicIssueCode, number>> = {
  VOLTAGE_DAMAGE: 5,
  MISSING_PSU: 4,
  BUDGET_EXCEEDED: 3,
  POE_INCOMPATIBLE: 2,
  UNPOWERED: 1,
};

type PowerCandidate = {
  kind: DeviceLogicState['winningInputKind'];
  voltage: number;
  watts: number;
  inputId?: string;
};

function dcInputsForVoltage(inputs: PowerInput[], voltage: number): PowerInput[] {
  return activePowerInputs(inputs).filter((i) => {
    if (i.kind !== 'dc-jack' && i.kind !== 'dc-terminal' && i.kind !== 'battery') return false;
    const min = i.voltageMin ?? 0;
    const max = i.voltageMax ?? 999;
    return voltage >= min && voltage <= max;
  });
}

function tagDcSupply(model: DeviceModel, voltage: number): string | undefined {
  const live = activePowerInputs(model.powerInputs);
  const match = dcInputsForVoltage(live, voltage)[0];
  return (match || live.find((i) => i.kind === 'dc-jack' || i.kind === 'dc-terminal' || i.kind === 'battery'))?.id;
}

function poeInInputId(model: DeviceModel, port: FacedPort): string | undefined {
  const face = port.face || 'front';
  const live = activePowerInputs(model.powerInputs);
  return (
    live.find(
      (i) => i.kind === 'poe-in' && i.portNumber === port.number && (i.face || 'front') === face
    ) || live.find((i) => i.kind === 'poe-in' && i.portNumber === port.number)
  )?.id;
}

function poeInPortEnabled(model: DeviceModel, port: FacedPort): boolean {
  const face = port.face || 'front';
  const bound =
    model.powerInputs.find(
      (i) => i.kind === 'poe-in' && i.portNumber === port.number && (i.face || 'front') === face
    ) || model.powerInputs.find((i) => i.kind === 'poe-in' && i.portNumber === port.number);
  if (!bound) return true;
  return isPowerInputEnabled(bound);
}

function pickWinningCandidate(model: DeviceModel, candidates: PowerCandidate[]): PowerCandidate | undefined {
  if (!candidates.length) return undefined;
  const byVoltage = [...candidates].sort((a, b) => b.voltage - a.voltage);
  if ((model.powerSelect || 'highest-voltage') !== 'listed-order') return byVoltage[0];
  for (const input of activePowerInputs(model.powerInputs)) {
    const hit = candidates.find((c) => c.inputId === input.id);
    if (hit) return hit;
  }
  return byVoltage[0];
}

/** One power-failure error per device: keep the most specific reason, drop UNPOWERED + duplicate PoE. */
function coalescePowerIssues(issues: LogicIssue[]): LogicIssue[] {
  const byEq = new Map<string, LogicIssue[]>();
  const rest: LogicIssue[] = [];
  for (const i of issues) {
    if (i.equipmentId && POWER_FAIL_PRIORITY[i.code]) {
      const list = byEq.get(i.equipmentId) || [];
      list.push(i);
      byEq.set(i.equipmentId, list);
    } else {
      rest.push(i);
    }
  }
  const picked: LogicIssue[] = [];
  byEq.forEach((list) => {
    const bestPri = Math.max(...list.map((i) => POWER_FAIL_PRIORITY[i.code] || 0));
    const candidates = list.filter((i) => (POWER_FAIL_PRIORITY[i.code] || 0) === bestPri);
    const chosen =
      candidates.find((i) => i.details && i.cableId) ||
      candidates.find((i) => i.details) ||
      candidates[0];
    picked.push(chosen);
  });
  return [...rest, ...picked];
}

function psuById(id?: string | null) {
  if (!id) return undefined;
  return PSU_CATALOG.find((p) => p.equipmentId === id) || getCatalogDeviceModel(id);
}

function isCca(subtype?: string) {
  return /cca/i.test(subtype || '');
}
function isDac(subtype?: string) {
  return /\bdac\b/i.test(subtype || '');
}

function cableIssueLabel(cable: Cable): string {
  const name = cable.label?.trim();
  const kind = cable.subtype?.trim();
  if (name && kind && name.toLowerCase() !== kind.toLowerCase()) return `${name} · ${kind}`;
  return name || kind || `${cable.type} cable`;
}

type SolverNode = ResolvedDevice & {
  idle: boolean;
  inTopology: boolean;
};

type EndpointRef = {
  nodeId: string;
  portNumber: number;
  kind?: string;
  face?: string;
};

type GraphEdge = {
  cable: Cable;
  a: EndpointRef;
  b: EndpointRef;
};

function findPort(node: SolverNode, ref: EndpointRef): FacedPort | undefined {
  const kind = ref.kind ? normalizePortKind(ref.kind) : undefined;
  const face = (ref.face as EquipmentFace | undefined) || undefined;
  const matches = node.ports.filter((p) => p.number === ref.portNumber);
  if (kind) {
    const k = matches.find((p) => normalizePortKind(String(p.kind)) === kind && (!face || p.face === face));
    if (k) return k;
  }
  if (face) {
    const f = matches.find((p) => p.face === face);
    if (f) return f;
  }
  return matches[0];
}

function parseEndpoint(endpoint: string, nodes: Map<string, SolverNode>): EndpointRef | null {
  const parsed = parsePortEndpointId(endpoint);
  const nodeId = parsed?.equipmentId || placedIdFromEndpoint(endpoint);
  if (!nodes.has(nodeId)) {
    const prefix = Array.from(nodes.keys()).find((id) => nodeId === id || nodeId.startsWith(`${id}:`) || id.startsWith(`${nodeId}:`));
    if (!prefix) return { nodeId, portNumber: parsed?.number ?? 0, kind: parsed?.kind, face: parsed?.face };
    return { nodeId: prefix, portNumber: parsed?.number ?? 0, kind: parsed?.kind, face: parsed?.face };
  }
  return { nodeId, portNumber: parsed?.number ?? 0, kind: parsed?.kind, face: parsed?.face };
}

function offeredForVoltage(
  port: FacedPort,
  inputKind: PowerInputKind | 'assigned-psu' | 'cabled-psu' | 'ac' | undefined,
  voltage: number | undefined,
  powered: boolean
): PoEStandard[] {
  const spec = port.poeOut;
  if (!spec || !powered) return [];
  if (spec.poeOutOnlyWhenPoweredBy && spec.poeOutOnlyWhenPoweredBy.length) {
    const kind = inputKind === 'assigned-psu' || inputKind === 'cabled-psu' ? 'dc-jack' : inputKind;
    const allowed = spec.poeOutOnlyWhenPoweredBy.some((k) => k === kind || (k === 'dc-jack' && (inputKind === 'assigned-psu' || inputKind === 'cabled-psu')) || (k === 'dc-terminal' && (inputKind === 'assigned-psu' || inputKind === 'cabled-psu')));
    if (!allowed) return [];
  }
  if (spec.requireInputVoltageIn && voltage != null) {
    const [min, max] = spec.requireInputVoltageIn;
    if (voltage < min || voltage > max) return [];
  }
  if (spec.outputFollowsInputVoltage && voltage != null) {
    if (voltage < 30) return spec.standards.filter((s) => s === 'passive-24');
    if (voltage < 44) return spec.standards.filter((s) => s === 'passive-24' || s === 'passive-48');
    return spec.standards.filter((s) => s !== 'passive-24');
  }
  return [...spec.standards];
}

function bestStandard(offered: PoEStandard[], accepted: PoEStandard[]): PoEStandard | null {
  const rank: PoEStandard[] = ['802.3bt', '802.3at', '802.3af', 'passive-48', 'passive-24'];
  for (const s of rank) {
    if (offered.includes(s) && accepted.includes(s)) return s;
  }
  return null;
}

function capSupplyWatts(input: PowerInput | undefined, voltage: number, watts: number): number {
  const cap = input ? inputCapacityWatts(input, voltage) : undefined;
  return cap != null ? Math.min(watts, cap) : watts;
}

function inputById(model: DeviceModel, id?: string): PowerInput | undefined {
  if (!id) return undefined;
  return model.powerInputs.find((i) => i.id === id);
}

function pdDrawWatts(port: FacedPort, standard: PoEStandard, model?: DeviceModel): number {
  const caps = [POE_DEFAULT_WATTS[standard]];
  if (port.poeIn?.maxWatts) caps.push(port.poeIn.maxWatts);
  if (model) {
    const bound = inputById(model, poeInInputId(model, port));
    const cap = bound ? inputCapacityWatts(bound, POE_NOMINAL_VOLTAGE[standard]) : undefined;
    if (cap != null) caps.push(cap);
  }
  return Math.min(...caps);
}

function voltageDamages(offered: PoEStandard, accepted: { voltageMin: number; voltageMax: number }): boolean {
  const v = POE_NOMINAL_VOLTAGE[offered];
  return v < accepted.voltageMin - 4 || v > accepted.voltageMax + 8;
}

function instanceOverride(node: SolverNode, port: FacedPort) {
  const key = portLogicKey(port.face, port.number, String(port.kind));
  const alt = portLogicKey(port.face, port.number);
  return node.config?.portLogic?.[key] || node.config?.portLogic?.[alt];
}

function collectNodes(design: Pick<Design, 'equipment' | 'racks' | 'cables'>): Map<string, SolverNode> {
  const map = new Map<string, SolverNode>();
  (design.equipment || []).forEach((eq: PlacedEquipment) => {
    const r = resolvePlacedEquipment(eq);
    map.set(r.nodeId, { ...r, idle: true, inTopology: false });
  });
  (design.racks || []).forEach((rack: Rack) => {
    (rack.equipment || []).forEach((item, index) => {
      const r = resolveRackEquipment(rack.id, item, index);
      map.set(r.nodeId, { ...r, idle: true, inTopology: false });
    });
  });
  return map;
}

function buildEdges(cables: Cable[], nodes: Map<string, SolverNode>): GraphEdge[] {
  const edges: GraphEdge[] = [];
  cables.forEach((cable) => {
    if (cable.hidden) return;
    const a = parseEndpoint(cable.from, nodes);
    const b = parseEndpoint(cable.to, nodes);
    if (!a || !b) return;
    edges.push({ cable, a, b });
  });
  return edges;
}

function otherEnd(edge: GraphEdge, nodeId: string): EndpointRef {
  return edge.a.nodeId === nodeId ? edge.b : edge.a;
}

function thisEnd(edge: GraphEdge, nodeId: string): EndpointRef {
  return edge.a.nodeId === nodeId ? edge.a : edge.b;
}

function cablesOnPort(edges: GraphEdge[], nodeId: string, port: FacedPort): GraphEdge[] {
  return edges.filter((e) => {
    const ends = [e.a, e.b];
    return ends.some((end) => {
      if (end.nodeId !== nodeId || end.portNumber !== port.number) return false;
      if (end.kind && normalizePortKind(end.kind) !== normalizePortKind(String(port.kind))) return false;
      if (end.face && end.face !== port.face) return false;
      return true;
    });
  });
}

function passthroughFarEnds(node: SolverNode, port: FacedPort, edges: GraphEdge[]): EndpointRef[] {
  if (!port.passthrough && !node.model.isPassthroughDevice) return [];
  const onPort = cablesOnPort(edges, node.nodeId, port);
  return onPort.map((e) => otherEnd(e, node.nodeId));
}

export function solveNetworkLogic(design: Pick<Design, 'equipment' | 'racks' | 'cables'>): LogicResult {
  const issues: LogicIssue[] = [];
  const nodes = collectNodes(design);
  const edges = buildEdges(design.cables || [], nodes);

  const degree = new Map<string, number>();
  edges.forEach((e) => {
    degree.set(e.a.nodeId, (degree.get(e.a.nodeId) || 0) + 1);
    degree.set(e.b.nodeId, (degree.get(e.b.nodeId) || 0) + 1);
  });
  nodes.forEach((n) => {
    const d = degree.get(n.nodeId) || 0;
    n.idle = d === 0;
    n.inTopology = d > 0;
  });

  type Offer = { standards: PoEStandard[]; wattsCap: number; voltage?: number };
  let offers = new Map<string, Offer>();
  let powered = new Map<string, boolean>();
  let winKind = new Map<string, DeviceLogicState['winningInputKind']>();
  let winVolt = new Map<string, number>();
  let winWatts = new Map<string, number>();
  let poeUsed = new Map<string, number>();
  let negotiated = new Map<string, { standard: PoEStandard; watts: number }>();

  const portKey = (nodeId: string, port: FacedPort) => `${nodeId}:${port.face}:${port.number}:${port.kind}`;

  const markPowered = (node: SolverNode, value: boolean) => {
    if (node.model.isPassthroughDevice) {
      powered.set(node.nodeId, true);
      return;
    }
    powered.set(node.nodeId, value);
  };

  nodes.forEach((n) => markPowered(n, false));

  const designHasInverter = Array.from(nodes.values()).some((n) => n.model.isInverter);
  const inverterHasStartBattery = new Map<string, boolean>();
  nodes.forEach((node) => {
    if (!node.model.isInverter || node.excluded) return;
    if (!inverterRequiresBatteryToStart(node.model)) {
      inverterHasStartBattery.set(node.nodeId, true);
      return;
    }
    const bat = inverterBatteryPorts(node);
    if (!bat) {
      inverterHasStartBattery.set(node.nodeId, false);
      return;
    }
    const reduced = analyzeFamilyAtPair(nodes, design.cables || [], 'bat', bat.plusKey, bat.minusKey, () => {});
    inverterHasStartBattery.set(node.nodeId, !!(reduced.branch && reduced.branch.ah > 0));
  });

  for (let iter = 0; iter < 16; iter++) {
    const nextOffers = new Map<string, Offer>();
    const nextPowered = new Map<string, boolean>();
    const nextUsed = new Map<string, number>();
    const nextNeg = new Map<string, { standard: PoEStandard; watts: number }>();

    nodes.forEach((node) => {
      if (node.excluded) {
        nextPowered.set(node.nodeId, false);
        return;
      }

      if (
        node.model.isInverter &&
        inverterRequiresBatteryToStart(node.model) &&
        !inverterHasStartBattery.get(node.nodeId)
      ) {
        nextPowered.set(node.nodeId, false);
        return;
      }

      const candidates: PowerCandidate[] = [];

      if (node.model.isMainsOutlet) {
        const acOut = derivedAcOutput(node.model);
        candidates.push({
          kind: 'ac',
          voltage: 48,
          watts: acOut?.watts || 3600,
        });
      }

      const nodeHasAcPowerCable = edges.some((edge) => {
        if (edge.cable.type !== 'power') return false;
        if (edge.a.nodeId !== node.nodeId && edge.b.nodeId !== node.nodeId) return false;
        const localRef = edge.a.nodeId === node.nodeId ? edge.a : edge.b;
        const farRef = otherEnd(edge, node.nodeId);
        const localPort = findPort(node, localRef);
        const farNode = nodes.get(farRef.nodeId);
        if (!localPort || !farNode) return false;
        const farPort = findPort(farNode, farRef);
        return powerPortFamily(localPort.kind) === 'ac' && powerCablesCompatible(localPort.kind, farPort?.kind);
      });

      // Inverters may assume grid AC-in. Everyone else:
      // - AC power cable present → supply only via that cable (no wall bypass)
      // - no AC cable → wall AC only when there is no inverter, or a live inverter
      const mainsAcLive =
        !designHasInverter ||
        Array.from(nodes.values()).some((n) => n.model.isInverter && !n.excluded && powered.get(n.nodeId));
      const assumeWallAc =
        node.model.isInverter || (!nodeHasAcPowerCable && mainsAcLive);

      node.model.powerInputs.forEach((input) => {
        if (!isPowerInputEnabled(input)) return;
        if (input.kind === 'ac') {
          if (!assumeWallAc) return;
          const v = typicalVoltageOfInput(input) ?? 230;
          candidates.push({
            kind: 'ac',
            voltage: 48,
            watts: inputCapacityWatts(input, v) || 200,
            inputId: input.id,
          });
        }
      });

      if (node.model.isPsu && assumeWallAc) {
        const out = derivedPsuOutput(node.model);
        candidates.push({
          kind: 'ac',
          voltage: out?.voltage || 48,
          watts: out?.watts || 60,
        });
      }

      if (node.model.isSolar || node.model.isBattery) {
        candidates.push({
          kind: 'ac',
          voltage: node.model.nominalVoltage || node.model.pvVmp || 48,
          watts: 100000,
        });
      }

      const assignedId = node.config?.assignedPsuId || node.model.shipsWithPsuId;
      const assigned = psuById(assignedId);
      const assignedOut = assigned ? derivedPsuOutput(assigned) : undefined;
      if (assignedOut && assumeWallAc) {
        const dcId = tagDcSupply(node.model, assignedOut.voltage);
        if (dcId) {
          candidates.push({
            kind: 'assigned-psu',
            voltage: assignedOut.voltage,
            watts: capSupplyWatts(inputById(node.model, dcId), assignedOut.voltage, assignedOut.watts),
            inputId: dcId,
          });
        }
      }

      edges.forEach((edge) => {
        if (edge.cable.type !== 'power') return;
        if (edge.a.nodeId !== node.nodeId && edge.b.nodeId !== node.nodeId) return;
        const localRef = edge.a.nodeId === node.nodeId ? edge.a : edge.b;
        const far = otherEnd(edge, node.nodeId);
        const src = nodes.get(far.nodeId);
        if (!src || src.excluded || !powered.get(src.nodeId)) return;
        const localPort = findPort(node, localRef);
        const srcPort = findPort(src, far);
        if (!powerCablesCompatible(localPort?.kind, srcPort?.kind)) return;

        const family = powerPortFamily(localPort?.kind);
        if (family === 'ac') {
          const acOut = derivedAcOutput(src.model);
          if (!acOut) return;
          const acIn =
            node.model.powerInputs.find(
              (i) =>
                i.kind === 'ac' &&
                isPowerInputEnabled(i) &&
                (i.portNumber == null || i.portNumber === localPort?.number)
            ) || node.model.powerInputs.find((i) => i.kind === 'ac' && isPowerInputEnabled(i));
          if (!acIn) return;
          const v = typicalVoltageOfInput(acIn) ?? acOut.voltage;
          candidates.push({
            kind: 'ac',
            voltage: 48,
            watts: capSupplyWatts(acIn, v, acOut.watts),
            inputId: acIn.id,
          });
          return;
        }

        const srcOut = derivedPsuOutput(src.model);
        if (!srcOut) return;
        const dcId = tagDcSupply(node.model, srcOut.voltage);
        if (!dcId) return;
        candidates.push({
          kind: 'cabled-psu',
          voltage: srcOut.voltage,
          watts: capSupplyWatts(inputById(node.model, dcId), srcOut.voltage, srcOut.watts),
          inputId: dcId,
        });
      });

      node.ports.forEach((port) => {
        if (!port.poeIn) return;
        if (!poeInPortEnabled(node.model, port)) return;
        const neighbors: EndpointRef[] = [];
        cablesOnPort(edges, node.nodeId, port).forEach((e) => neighbors.push(otherEnd(e, node.nodeId)));
        passthroughFarEnds(node, port, edges).forEach((n) => neighbors.push(n));

        const walk = (ref: EndpointRef, hops = 0) => {
          if (hops > 8) return;
          const other = nodes.get(ref.nodeId);
          if (!other) return;
          if (other.excluded) return;
          if (other.model.isPassthroughDevice || other.ports.find((p) => p.number === ref.portNumber)?.passthrough) {
            const p = findPort(other, ref);
            if (!p) return;
            cablesOnPort(edges, other.nodeId, p).forEach((e) => {
              const far = otherEnd(e, other.nodeId);
              if (far.nodeId === node.nodeId) return;
              walk(far, hops + 1);
            });
            return;
          }
          const p = findPort(other, ref);
          if (!p) return;
          const off = offers.get(portKey(other.nodeId, p));
          if (!off || !off.standards.length) return;
          const match = bestStandard(off.standards, port.poeIn!.standards);
          if (!match) return;
          if (voltageDamages(match, port.poeIn!)) return;
          const watts = Math.min(off.wattsCap, pdDrawWatts(port, match, node.model), POE_DEFAULT_WATTS[match]);
          candidates.push({
            kind: 'poe-in',
            voltage: POE_NOMINAL_VOLTAGE[match],
            watts,
            inputId: poeInInputId(node.model, port),
          });
        };
        neighbors.forEach((n) => walk(n));
      });

      const win = pickWinningCandidate(node.model, candidates);
      const need = selfDrawWattsAtVoltage(node.model, win?.voltage);
      const isOn = !!win && win.watts >= need;
      nextPowered.set(node.nodeId, node.model.isPassthroughDevice ? true : isOn);
      if (win) {
        winKind.set(node.nodeId, win.kind);
        winVolt.set(node.nodeId, win.voltage);
        winWatts.set(node.nodeId, win.watts);
      }

      let budget = isOn && win ? Math.max(0, win.watts - need) : 0;
      let used = 0;

      const poeOutPorts = [...node.ports]
        .filter((p) => p.poeOut)
        .sort((a, b) => a.number - b.number || String(a.kind).localeCompare(String(b.kind)));

      poeOutPorts.forEach((port) => {
        const override = instanceOverride(node, port);
        let standards = offeredForVoltage(port, win?.kind, win?.voltage, isOn);
        if (override?.poeMode === 'off') standards = [];
        if (override?.poeMode === 'forced' && override.forcedStandard) {
          if (!standards.includes(override.forcedStandard)) {
            standards = [];
          } else {
            standards = [override.forcedStandard];
          }
        }
        nextOffers.set(portKey(node.nodeId, port), {
          standards,
          wattsCap: Math.min(port.poeOut!.perPortMaxWatts, budget),
          voltage: win?.voltage,
        });
      });

      poeOutPorts.forEach((port) => {
        const off = nextOffers.get(portKey(node.nodeId, port));
        if (!off || !off.standards.length) return;

        const walkPd = (ref: EndpointRef, hops = 0): { node: SolverNode; port: FacedPort } | null => {
          if (hops > 8) return null;
          const other = nodes.get(ref.nodeId);
          if (!other) return null;
          if (other.excluded) return null;
          const p = findPort(other, ref);
          if (!p) return null;
          if (other.model.isPassthroughDevice || p.passthrough) {
            const others = cablesOnPort(edges, other.nodeId, p)
              .map((e) => otherEnd(e, other.nodeId))
              .filter((x) => x.nodeId !== node.nodeId);
            for (const nref of others) {
              const found = walkPd(nref, hops + 1);
              if (found) return found;
            }
            return null;
          }
          if (p.poeIn) return { node: other, port: p };
          return null;
        };

        const neighbors = cablesOnPort(edges, node.nodeId, port).map((e) => otherEnd(e, node.nodeId));
        neighbors.forEach((nref) => {
          const pd = walkPd(nref);
          if (!pd || pd.node.excluded) return;
          const match = bestStandard(off.standards, pd.port.poeIn!.standards);
          if (!match) return;
          const required = pd.port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(pd.node.model);
          const classW = POE_DEFAULT_WATTS[match];
          const available = Math.min(port.poeOut!.perPortMaxWatts, classW);
          if (required > available + 0.01) {
            return;
          }
          if (required > budget + 0.01) {
            return;
          }
          budget -= required;
          used += required;
          nextNeg.set(portKey(node.nodeId, port), { standard: match, watts: required });
          nextNeg.set(portKey(pd.node.nodeId, pd.port), { standard: match, watts: required });
        });
      });

      nextUsed.set(node.nodeId, used);
    });

    let changed = false;
    nodes.forEach((n) => {
      if (!!powered.get(n.nodeId) !== !!nextPowered.get(n.nodeId)) changed = true;
    });
    nextOffers.forEach((v, k) => {
      const prev = offers.get(k);
      if (!prev || prev.standards.join() !== v.standards.join() || prev.wattsCap !== v.wattsCap) changed = true;
    });
    powered = nextPowered;
    offers = nextOffers;
    poeUsed = nextUsed;
    negotiated = nextNeg;
    if (!changed) break;
    if (iter === 15) {
      issues.push(issue('UNSTABLE', 'warning', 'power', 'Power state did not settle (possible PoE loop).'));
    }
  }

  const devices: Record<string, DeviceLogicState> = {};
  const ports: Record<string, PortLogicState> = {};
  const cables: Record<string, CableLogicState> = {};

  const uniqueIssues = new Map<string, LogicIssue>();
  const addIssue = (i: LogicIssue) => {
    const key = `${i.code}:${i.equipmentId || ''}:${i.cableId || ''}:${i.portNumber || ''}:${i.message}`;
    if (!uniqueIssues.has(key)) uniqueIssues.set(key, { ...i, id: key });
  };
  issues.forEach(addIssue);

  nodes.forEach((node) => {
    const on = !!powered.get(node.nodeId);
    const active = on && !node.excluded;
    const inputW = winWatts.get(node.nodeId);
    const used = poeUsed.get(node.nodeId) || 0;
    const ratedDraw = selfDrawWattsAtVoltage(node.model, winVolt.get(node.nodeId));
    const draw = active ? ratedDraw : 0;
    const budgetTotal = active && inputW != null ? Math.max(0, inputW - ratedDraw) : 0;
    const isPse = active && node.ports.some((p) => !!p.poeOut);
    devices[node.nodeId] = {
      nodeId: node.nodeId,
      equipmentId: node.nodeId,
      name: node.name,
      catalogId: node.catalogId,
      type: node.type,
      excluded: node.excluded,
      poweredOff: !!node.config?.poweredOff,
      unmodeled: node.unmodeled,
      idle: node.idle,
      powered: on,
      inTopology: node.inTopology,
      inputVoltage: active ? winVolt.get(node.nodeId) : undefined,
      // Solar/battery use a sentinel capacity internally; don't surface it as supply watts.
      inputWatts: active && !node.model.isSolar && !node.model.isBattery ? inputW : undefined,
      selfDrawWatts: draw,
      ratedSelfDrawWatts: ratedDraw,
      poeBudgetWatts: isPse ? budgetTotal : 0,
      poeUsedWatts: active ? used : 0,
      isPse,
      winningInputKind: active ? winKind.get(node.nodeId) : undefined,
    };

    node.ports.forEach((port) => {
      const off = offers.get(portKey(node.nodeId, port));
      const neg = negotiated.get(portKey(node.nodeId, port));
      let caption = '';
      if (neg) caption = `${POE_STANDARD_LABEL[neg.standard]} ${neg.watts}W`;
      else if (off?.standards.length) caption = off.standards.map((s) => POE_STANDARD_LABEL[s]).join('/');
      else if (port.passthrough) caption = 'pass';
      else if (port.poeOut) caption = 'off';
      else if (port.poeIn) caption = 'PoE-in';
      ports[portKey(node.nodeId, port)] = {
        nodeId: node.nodeId,
        portNumber: port.number,
        kind: String(port.kind),
        face: port.face,
        offeredStandards: off?.standards || [],
        negotiated: neg || null,
        caption,
      };
    });

    if (node.config?.deviceModel && !node.unmodeled) {
      const cmp = compareLayoutToModel(node.config.deviceModel, node.config);
      if (!cmp.ok) {
        addIssue(
          issue('LAYOUT_LOGIC_MISMATCH', node.inTopology ? 'warning' : 'info', 'ports', `${node.name}: layout and electrical model disagree.`, {
            equipmentId: node.nodeId,
            details: cmp.details,
            idle: node.idle,
          })
        );
      }
    }

    if (node.excluded || node.idle) {
      if (node.config?.poweredOff && !node.idle) {
        addIssue(
          issue('UNPOWERED', 'info', 'power', `${node.name} is turned off (excluded from Logic).`, {
            equipmentId: node.nodeId,
            details: 'Turn it on to include it in the simulation again.',
          })
        );
      }
      return;
    }

    if (node.unmodeled && node.inTopology) {
      addIssue(
        issue('UNMODELED', 'warning', 'other', `${node.name} has no electrical model. Ports are treated as data-only.`, {
          equipmentId: node.nodeId,
          details: 'Click the device, then Edit Port Layout on the bottom panel.',
        })
      );
    }

    node.ports.forEach((port) => {
      const override = instanceOverride(node, port);
      if (override?.poeMode === 'forced' && override.forcedStandard && port.poeOut) {
        const offered = offeredForVoltage(port, winKind.get(node.nodeId), winVolt.get(node.nodeId), on);
        if (!offered.includes(override.forcedStandard)) {
          addIssue(
            issue(
              'FORCED_INVALID',
              'error',
              'poe',
              `${node.name} port ${port.label || port.number}: forced ${POE_STANDARD_LABEL[override.forcedStandard]} is not available on this supply.`,
              {
                equipmentId: node.nodeId,
                portNumber: port.number,
                details: offered.length
                  ? `This port can currently offer ${offered.map((s) => POE_STANDARD_LABEL[s]).join('/')}.`
                  : 'This port is not sourcing PoE at the current input voltage.',
              }
            )
          );
        }
      }
    });

    const liveInputs = activePowerInputs(node.model.powerInputs);
    const hasDc = liveInputs.some((i) => i.kind === 'dc-jack' || i.kind === 'dc-terminal');
    const hasAc = liveInputs.some((i) => i.kind === 'ac') || node.model.isPsu;
    const hasAssigned = !!(node.config?.assignedPsuId || node.model.shipsWithPsuId);
    const hasPoeIn = node.ports.some((p) => p.poeIn);
    const linkedCount = edges.filter((e) => e.a.nodeId === node.nodeId || e.b.nodeId === node.nodeId).length;
    if (!on && hasDc && !hasAc && !hasAssigned && node.inTopology && !node.model.isPassthroughDevice && !node.model.isSolar && !node.model.isBattery && !node.model.isInverter) {
      addIssue(
        issue('MISSING_PSU', 'error', 'power', `${node.name} needs a PSU assigned or cabled (DC input, no supply).`, {
          equipmentId: node.nodeId,
          details: linkedCount
            ? 'Data/power cables are present, but nothing is feeding this device’s DC input.'
            : 'Assign a PSU in the device panel, or cable a DC supply to its DC port.',
        })
      );
    } else if (!on && node.inTopology && !node.model.isPassthroughDevice && !node.model.isSolar && !node.model.isBattery) {
      let message = `${node.name} is not powered.`;
      let details = 'No compatible power path (PSU, AC, or PoE-in) met the device draw.';
      const drawW = Math.round(selfDrawWattsAtVoltage(node.model) * 10) / 10;
      if (hasPoeIn && linkedCount > 0) {
        message = `${node.name} is not powered over PoE.`;
        details = `Connected, but no compatible PoE path met its ${drawW} W draw (wrong standard, too little wattage, or switch budget).`;
      } else if (hasPoeIn && linkedCount === 0) {
        details = `Needs PoE-in (~${drawW} W). Cable it to a PoE switch/injector port that can supply that.`;
      } else if (linkedCount > 0) {
        details = 'Cabled, but no AC/DC/PSU supply is feeding this device.';
      }
      addIssue(
        issue('UNPOWERED', 'error', 'power', message, {
          equipmentId: node.nodeId,
          details,
        })
      );
    }

    node.ports.forEach((port) => {
      if (on || !port.poeIn || !node.inTopology || node.excluded) return;
      const neighbors = cablesOnPort(edges, node.nodeId, port).map((e) => otherEnd(e, node.nodeId));
      neighbors.forEach((nref) => {
        const walkPse = (ref: EndpointRef, hops = 0): { node: SolverNode; port: FacedPort } | null => {
          if (hops > 8) return null;
          const other = nodes.get(ref.nodeId);
          if (!other || other.excluded) return null;
          const p = findPort(other, ref);
          if (!p) return null;
          if (other.model.isPassthroughDevice || p.passthrough) {
            const others = cablesOnPort(edges, other.nodeId, p)
              .map((e) => otherEnd(e, other.nodeId))
              .filter((x) => x.nodeId !== node.nodeId);
            for (const far of others) {
              const found = walkPse(far, hops + 1);
              if (found) return found;
            }
            return null;
          }
          if (p.poeOut) return { node: other, port: p };
          return null;
        };
        const pse = walkPse(nref);
        if (!pse) {
          const other = nodes.get(nref.nodeId);
          if (other && !other.excluded) {
            const need = port.poeIn!.standards.map((s) => POE_STANDARD_LABEL[s]).join('/');
            const needW = port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(node.model);
            addIssue(
              issue(
                'POE_INCOMPATIBLE',
                'error',
                'poe',
                `${node.name} is not powered: needs ${need} (~${Math.round(needW * 10) / 10} W), but ${other.name} has no PoE-out on that port.`,
                {
                  equipmentId: node.nodeId,
                  portNumber: port.number,
                  details: `${other.name} is data-only here. Use a PoE switch, injector, or a different port that can supply ${need}.`,
                }
              )
            );
          }
          return;
        }
        const off = offers.get(portKey(pse.node.nodeId, pse.port));
        const match = off ? bestStandard(off.standards, port.poeIn!.standards) : null;
        if (!match) {
          const need = port.poeIn!.standards.map((s) => POE_STANDARD_LABEL[s]).join('/');
          const have = off?.standards.length ? off.standards.map((s) => POE_STANDARD_LABEL[s]).join('/') : 'no PoE';
          const needW = port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(node.model);
          addIssue(
            issue(
              'POE_INCOMPATIBLE',
              'error',
              'poe',
              `${node.name} is not powered: needs ${need} (~${Math.round(needW * 10) / 10} W), ${pse.node.name} offers ${have}.`,
              {
                equipmentId: node.nodeId,
                portNumber: port.number,
                details: `${pse.node.name} port ${pse.port.label || pse.port.number} → ${node.name}. Standards do not overlap.`,
              }
            )
          );
        } else if (voltageDamages(match, port.poeIn!)) {
          addIssue(
            issue(
              'VOLTAGE_DAMAGE',
              'error',
              'poe',
              `${node.name} can be damaged: ${POE_STANDARD_LABEL[match]} is outside its ${port.poeIn!.voltageMin}–${port.poeIn!.voltageMax}V window.`,
              { equipmentId: node.nodeId, portNumber: port.number }
            )
          );
        } else {
          const needW = port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(node.model);
          const classW = POE_DEFAULT_WATTS[match];
          const portCap = Math.min(pse.port.poeOut!.perPortMaxWatts, classW);
          const pseUsed = poeUsed.get(pse.node.nodeId) || 0;
          const pseBudget = devices[pse.node.nodeId]?.poeBudgetWatts || 0;
          const remaining = Math.max(0, pseBudget - pseUsed);
          if (needW > portCap + 0.01) {
            addIssue(
              issue(
                'BUDGET_EXCEEDED',
                'error',
                'poe',
                `${node.name} is not powered: needs ~${Math.round(needW * 10) / 10} W, but ${pse.node.name} port ${pse.port.label || pse.port.number} only offers ~${Math.round(portCap * 10) / 10} W (${POE_STANDARD_LABEL[match]}).`,
                {
                  equipmentId: node.nodeId,
                  portNumber: port.number,
                  details: `Use a higher-watt PoE standard/port (≥ ${Math.round(needW * 10) / 10} W), or lower this device’s PoE draw.`,
                }
              )
            );
          } else if (!negotiated.get(portKey(node.nodeId, port)) && powered.get(pse.node.nodeId)) {
            addIssue(
              issue(
                'BUDGET_EXCEEDED',
                'error',
                'poe',
                `${node.name} is not powered: needs ~${Math.round(needW * 10) / 10} W from ${pse.node.name}, but only ~${Math.round(remaining * 10) / 10} W PoE budget remains.`,
                {
                  equipmentId: node.nodeId,
                  portNumber: port.number,
                  details: `${pse.node.name} already uses ${Math.round(pseUsed * 10) / 10} W of ${Math.round(pseBudget * 10) / 10} W PoE budget.`,
                }
              )
            );
          }
        }
      });
    });
  });

  edges.forEach((edge) => {
    const { cable, a, b } = edge;
    const na = nodes.get(a.nodeId);
    const nb = nodes.get(b.nodeId);
    let status: CableLogicState['status'] = 'ok';
    const flag = (code: LogicIssueCode, severity: LogicSeverity, category: LogicIssueCategory, message: string, details?: string) => {
      addIssue(issue(code, severity, category, message, { cableId: cable.id, details, equipmentId: na?.nodeId }));
      if (severity === 'error') status = 'error';
      else if (status !== 'error') status = 'warning';
    };

    if (!na || !nb) {
      flag('DANGLING_CABLE', 'error', 'ports', 'Cable endpoint is missing (deleted port or device).');
      cables[cable.id] = { cableId: cable.id, status: 'error' };
      return;
    }

    if (na.excluded || nb.excluded) {
      status = 'excluded';
      const included = na.excluded ? nb : na;
      const excl = na.excluded ? na : nb;
      const end = na.excluded ? b : a;
      addIssue(
        issue('EXCLUDED_NEIGHBOR', 'info', 'other', `Port on ${included.name} connects to excluded ${excl.name} (not simulated).`, {
          equipmentId: included.nodeId,
          cableId: cable.id,
          portNumber: end.portNumber,
        })
      );
      cables[cable.id] = { cableId: cable.id, status };
      return;
    }

    const pa = findPort(na, a);
    const pb = findPort(nb, b);
    if (!pa || !pb) {
      flag('DANGLING_CABLE', 'error', 'ports', 'Cable is attached to a port that is no longer on the layout.');
      cables[cable.id] = { cableId: cable.id, status: 'error' };
      return;
    }

    const ka = normalizePortKind(String(pa.kind));
    const kb = normalizePortKind(String(pb.kind));

    if (cable.type === 'power') {
      if (!isPowerKind(ka) || !isPowerKind(kb)) {
        flag('CABLE_TYPE', 'error', 'physical', 'Power cable must land on DC/AC/PV/BAT ports.', `${na.name} (${ka}) ↔ ${nb.name} (${kb})`);
      } else if (!powerCablesCompatible(ka, kb)) {
        flag('POWER_PORT_ROLE', 'error', 'power', 'Power cable families do not match (PV, battery, AC, and DC stay separate).', `${na.name} (${ka}) ↔ ${nb.name} (${kb})`);
      }
    } else if (cable.type === 'fiber') {
      if (isCopperKind(ka) || isCopperKind(kb)) {
        flag('MEDIA_MISMATCH', 'error', 'physical', 'Fiber cable cannot terminate on RJ45.', `${na.name} ↔ ${nb.name}`);
      }
    } else {
      const dac = isDac(cable.subtype);
      if ((isFiberKind(ka) && isCopperKind(kb) && !dac) || (isFiberKind(kb) && isCopperKind(ka) && !dac)) {
        flag('MEDIA_MISMATCH', 'error', 'physical', 'Copper patch on an SFP/SFP+ cage (use fiber or DAC).', `${na.name} ${ka} ↔ ${nb.name} ${kb}`);
      }
      if (isPowerKind(ka) || isPowerKind(kb)) {
        flag('CABLE_TYPE', 'error', 'physical', 'Ethernet cable on a power-only port.');
      }
    }

    const speedsA = pa.speedsMbps || [];
    const speedsB = pb.speedsMbps || [];
    if (speedsA.length && speedsB.length) {
      const common = Math.max(0, ...speedsA.filter((s) => speedsB.includes(s)));
      if (common > 0 && common < 1000) {
        flag('SPEED_MISMATCH', 'warning', 'physical', `Link limited to ${common} Mbps.`, `${na.name} ↔ ${nb.name}`);
      }
    }

    const poeOnLink = !!(negotiated.get(portKey(na.nodeId, pa)) || negotiated.get(portKey(nb.nodeId, pb)) || pa.poeOut || pb.poeOut);
    if (poeOnLink && isCca(cable.subtype) && cable.type === 'ethernet') {
      flag('CABLE_TYPE', 'warning', 'physical', 'CCA copper with PoE — extra drop and heat. Prefer copper.', cableIssueLabel(cable));
    }

    const combo = pa.comboGroup;
    if (combo) {
      const siblings = na.ports.filter((p) => p.comboGroup === combo && p !== pa);
      const extra = siblings.some((p) => cablesOnPort(edges, na.nodeId, p).length > 0);
      if (extra) {
        flag('COMBO_CONFLICT', 'error', 'ports', `Combo cage on ${na.name}: RJ45 and SFP both cabled.`);
      }
    }

    if (pa.passthrough || na.model.isPassthroughDevice) {
      const n = cablesOnPort(edges, na.nodeId, pa).length;
      if (n < 2) {
        addIssue(
          issue('PASSTHROUGH_OPEN', 'info', 'ports', `${na.name} port ${pa.label || pa.number} has only one side connected.`, {
            equipmentId: na.nodeId,
            portNumber: pa.number,
            cableId: cable.id,
          })
        );
      }
    }

    const src = pa.poeOut ? { node: na, port: pa } : pb.poeOut ? { node: nb, port: pb } : null;
    const dst = pa.poeIn && !pa.poeOut ? { node: na, port: pa } : pb.poeIn && !pb.poeOut ? { node: nb, port: pb } : null;
    if (src && dst && !src.node.excluded && !dst.node.excluded && dst.node.inTopology) {
      const off = offers.get(portKey(src.node.nodeId, src.port));
      const match = off ? bestStandard(off.standards, dst.port.poeIn!.standards) : null;
      if (!match) {
        const need = dst.port.poeIn!.standards.map((s) => POE_STANDARD_LABEL[s]).join('/');
        const have = off?.standards.length ? off.standards.map((s) => POE_STANDARD_LABEL[s]).join('/') : 'no PoE';
        addIssue(
          issue(
            'POE_INCOMPATIBLE',
            'error',
            'poe',
            `${dst.node.name} is not powered: needs ${need} (~${Math.round((dst.port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(dst.node.model)) * 10) / 10} W), ${src.node.name} offers ${have}.`,
            {
              equipmentId: dst.node.nodeId,
              cableId: cable.id,
              portNumber: dst.port.number,
              details: `${src.node.name} port ${src.port.label || src.port.number} → ${dst.node.name}. Standards do not overlap.`,
            }
          )
        );
        status = 'error';
      } else if (voltageDamages(match, dst.port.poeIn!)) {
        addIssue(
          issue(
            'VOLTAGE_DAMAGE',
            'error',
            'poe',
            `${dst.node.name} can be damaged: ${POE_STANDARD_LABEL[match]} is outside its ${dst.port.poeIn!.voltageMin}–${dst.port.poeIn!.voltageMax}V window.`,
            { equipmentId: dst.node.nodeId, cableId: cable.id, portNumber: dst.port.number }
          )
        );
        status = 'error';
      } else if (!negotiated.get(portKey(src.node.nodeId, src.port)) && src.node.inTopology && powered.get(src.node.nodeId)) {
        const needW = dst.port.poeIn!.maxWatts ?? selfDrawWattsAtVoltage(dst.node.model);
        const matchStd = bestStandard(off?.standards || [], dst.port.poeIn!.standards);
        const classW = matchStd ? POE_DEFAULT_WATTS[matchStd] : 0;
        const portCap = matchStd ? Math.min(src.port.poeOut!.perPortMaxWatts, classW) : src.port.poeOut!.perPortMaxWatts;
        const pseUsed = poeUsed.get(src.node.nodeId) || 0;
        const pseBudget = devices[src.node.nodeId]?.poeBudgetWatts || 0;
        const remaining = Math.max(0, pseBudget - pseUsed);
        if (matchStd && needW > portCap + 0.01) {
          addIssue(
            issue(
              'BUDGET_EXCEEDED',
              'error',
              'poe',
              `${dst.node.name} is not powered: needs ~${Math.round(needW * 10) / 10} W, but ${src.node.name} port ${src.port.label || src.port.number} only offers ~${Math.round(portCap * 10) / 10} W (${POE_STANDARD_LABEL[matchStd]}).`,
              {
                equipmentId: dst.node.nodeId,
                cableId: cable.id,
                portNumber: dst.port.number,
                details: `Use a higher-watt PoE standard/port (≥ ${Math.round(needW * 10) / 10} W).`,
              }
            )
          );
        } else {
          addIssue(
            issue(
              'BUDGET_EXCEEDED',
              'error',
              'poe',
              `${dst.node.name} is not powered: needs ~${Math.round(needW * 10) / 10} W from ${src.node.name}, but only ~${Math.round(remaining * 10) / 10} W PoE budget remains.`,
              {
                equipmentId: dst.node.nodeId,
                cableId: cable.id,
                portNumber: dst.port.number,
                details: `${src.node.name} already uses ${Math.round(pseUsed * 10) / 10} W of ${Math.round(pseBudget * 10) / 10} W PoE budget.`,
              }
            )
          );
        }
        status = 'error';
      }
    }

    cables[cable.id] = { cableId: cable.id, status };
  });

  const pvIssue = (code: LogicIssueCode, severity: LogicSeverity, message: string, extras?: Partial<LogicIssue>) => {
    addIssue(issue(code, severity, 'power', message, extras));
  };
  nodes.forEach((node) => {
    if (node.excluded || !node.model.isInverter) return;
    node.model.powerInputs.filter((i) => i.kind === 'pv' && i.enabled !== false).forEach((input) => {
      const pair = inverterMpptPorts(node, input.mpptId || input.id);
      if (!pair) {
        pvIssue('PV_OPEN_STRING', 'error', `${node.name}: PV/MPPT input is missing a PV+ or PV− port.`, { equipmentId: node.nodeId });
        return;
      }
      const { branch } = analyzeFamilyAtPair(nodes, design.cables || [], 'pv', pair.plusKey, pair.minusKey, pvIssue);
      if (!branch) return;
      branch.warnings.forEach((w) => pvIssue('PV_MPPT_RANGE', 'warning', `${node.name}: ${w}`, { equipmentId: node.nodeId }));
      if (input.voltageMax != null && branch.voc > input.voltageMax) {
        pvIssue('PV_VOC_EXCEEDED', 'error', `${node.name}: string Voc ${Math.round(branch.voc)} V exceeds max ${input.voltageMax} V.`, { equipmentId: node.nodeId });
      }
      if (input.mpptVoltageMin != null && branch.vmp > 0 && branch.vmp < input.mpptVoltageMin) {
        pvIssue('PV_MPPT_RANGE', 'error', `${node.name}: string Vmp ${Math.round(branch.vmp)} V is below MPPT min ${input.mpptVoltageMin} V.`, { equipmentId: node.nodeId });
      }
      if (input.mpptVoltageMax != null && branch.vmp > input.mpptVoltageMax) {
        pvIssue('PV_MPPT_RANGE', 'error', `${node.name}: string Vmp ${Math.round(branch.vmp)} V is above MPPT max ${input.mpptVoltageMax} V.`, { equipmentId: node.nodeId });
      }
      if (input.maxWatts != null && branch.pmax > input.maxWatts) {
        pvIssue('PV_POWER_EXCEEDED', 'error', `${node.name}: array ${Math.round(branch.pmax)} W exceeds max PV ${input.maxWatts} W.`, { equipmentId: node.nodeId });
      }
      if (input.maxIsc != null && branch.isc > input.maxIsc) {
        pvIssue('PV_ISC_EXCEEDED', 'error', `${node.name}: Isc ${branch.isc.toFixed(1)} A exceeds max ${input.maxIsc} A.`, { equipmentId: node.nodeId });
      }
    });
    const bat = inverterBatteryPorts(node);
    if (bat) {
      const { branch } = analyzeFamilyAtPair(nodes, design.cables || [], 'bat', bat.plusKey, bat.minusKey, pvIssue);
      if (!branch || !(branch.ah > 0)) {
        const needsBattery = inverterRequiresBatteryToStart(node.model);
        pvIssue(
          'BACKUP_NO_STORAGE',
          needsBattery ? 'error' : 'warning',
          needsBattery
            ? `${node.name} requires a battery to start (no battery bank on BAT+/BAT−).`
            : `${node.name} has no battery bank on BAT+/BAT−.`,
          { equipmentId: node.nodeId }
        );
      } else if (bat.input?.voltageMin != null && bat.input.voltageMax != null) {
        const v = branch.volts || branch.voc;
        if (v < bat.input.voltageMin || v > bat.input.voltageMax) {
          pvIssue('BACKUP_VOLTAGE_MISMATCH', 'error', `${node.name}: battery bus ${Math.round(v)} V is outside ${bat.input.voltageMin}–${bat.input.voltageMax} V.`, { equipmentId: node.nodeId });
        }
      }
    } else if (inverterRequiresBatteryToStart(node.model)) {
      pvIssue(
        'BACKUP_NO_STORAGE',
        'error',
        `${node.name} requires a battery to start (missing BAT+/BAT− ports).`,
        { equipmentId: node.nodeId }
      );
    }
  });
  findSharedPvTrackers(nodes, design.cables || [], pvIssue);

  let poeUsedSum = 0;
  let poeBudgetSum = 0;
  let poweredN = 0;
  let unpoweredN = 0;
  let excludedN = 0;
  let idleN = 0;
  let inLogic = 0;
  Object.values(devices).forEach((d) => {
    if (d.excluded) {
      excludedN += 1;
      return;
    }
    inLogic += 1;
    if (d.idle) idleN += 1;
    if (d.powered) poweredN += 1;
    else if (d.inTopology) unpoweredN += 1;
    poeUsedSum += d.poeUsedWatts || 0;
    poeBudgetSum += d.poeBudgetWatts || 0;
  });

  const coalescedIssues = coalescePowerIssues(Array.from(uniqueIssues.values()));

  const loadWattsByNode = new Map<string, number>();
  nodes.forEach((node) => {
    if (node.excluded || node.model.isSolar || node.model.isBattery || node.model.isInverter || node.model.isPsu || node.model.isMainsOutlet) return;
    const d = devices[node.nodeId];
    if (!d?.powered) return;
    loadWattsByNode.set(node.nodeId, d.selfDrawWatts || 0);
  });
  const backupPower = summarizeBackupPower(nodes, design.cables || [], loadWattsByNode, coalescedIssues);
  backupPower.islands.forEach((island) => {
    const node = nodes.get(island.inverterId);
    if (!node) return;
    if (island.inverterAcWatts > 0 && island.loadWatts > island.inverterAcWatts) {
      addIssue(issue('INVERTER_OVERLOAD', 'error', 'power', `${island.inverterName}: AC load ${Math.round(island.loadWatts)} W exceeds ${island.inverterAcWatts} W output.`, { equipmentId: island.inverterId }));
    }
    const v = island.batteryVolts;
    const maxA = node.model.maxDischargeAmps;
    if (v > 0 && maxA != null && island.loadWatts > 0) {
      const dcA = island.loadWatts / island.efficiency / v;
      if (dcA > maxA) {
        addIssue(issue('INVERTER_OVERLOAD', 'error', 'power', `${island.inverterName}: battery current ${dcA.toFixed(0)} A exceeds ${maxA} A discharge limit.`, { equipmentId: island.inverterId }));
      }
    }
  });
  const finalIssues = coalescePowerIssues(Array.from(uniqueIssues.values()));

  const cableVals = Object.values(cables);
  const overview: LogicOverview = {
    devicesInLogic: inLogic,
    powered: poweredN,
    unpowered: unpoweredN,
    excluded: excludedN,
    idle: idleN,
    poeUsedWatts: Math.round(poeUsedSum * 10) / 10,
    poeBudgetWatts: Math.round(poeBudgetSum * 10) / 10,
    cablesOk: cableVals.filter((c) => c.status === 'ok').length,
    cablesFlagged: cableVals.filter((c) => c.status === 'error' || c.status === 'warning').length,
    missingPsu: finalIssues.filter((i) => i.code === 'MISSING_PSU').length,
  };

  return {
    issues: finalIssues,
    devices,
    ports,
    cables,
    overview,
    backupPower,
  };
}

export function healthErrors(result: LogicResult, hideIdle: boolean): LogicIssue[] {
  return result.issues.filter((i) => {
    if (i.severity !== 'error') return false;
    if (hideIdle && i.idle) return false;
    const dev = i.equipmentId ? result.devices[i.equipmentId] : undefined;
    if (hideIdle && dev?.idle && (i.code === 'UNPOWERED' || i.code === 'MISSING_PSU')) return false;
    return true;
  });
}
